<template>
  <div>
    <!-- Search Form Card -->
    <b-card :title="$t('ค้นหารายการกิจกรรมน่าสงสัย')">
      <b-form @submit.prevent="onSubmit">
        <b-row>
          <!-- Date Range -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('วันที่เริ่มต้น')"
              label-for="startDate"
            >
              <flat-pickr
                v-model="filters.startDate"
                class="form-control"
                :placeholder="$t('เลือกวันที่เริ่มต้น')"
                :config="dateConfig"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('วันที่สิ้นสุด')"
              label-for="endDate"
            >
              <flat-pickr
                v-model="filters.endDate"
                class="form-control"
                :placeholder="$t('เลือกวันที่สิ้นสุด')"
                :config="dateConfig"
              />
            </b-form-group>
          </b-col>

          <!-- Huay Type & Round ID -->
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('ประเภทหวย')"
              label-for="huayType"
            >
              <v-select
                v-model="filters.huayName"
                :options="$tOptions(gameTypes)"
                :get-option-label="$tDisplay"
                :placeholder="$t('เลือกประเภทหวย')"
                :clearable="true"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('Round ID')"
              label-for="roundId"
            >
              <b-form-input
                id="roundId"
                v-model="filters.roundId"
                :placeholder="$t('Round ID')"
              />
            </b-form-group>
          </b-col>

          <!-- Agent Selector - Master Only -->
          <b-col
            v-if="isMaster"
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('ตัวแทน')"
              label-for="agent"
            >
              <agent-selector
                v-model="selectedAgent"
                :placeholder="$t('พิมพ์เพื่อค้นหา...')"
              />
            </b-form-group>
          </b-col>

        </b-row>

        <div class="text-right mb-2">
          <b-button
            variant="primary"
            type="submit"
          >
            {{ $t('ค้นหา') }}
          </b-button>
        </div>

        <!-- Detection Type Section -->
        <div class="mb-2">
          <label class="d-block mb-1">{{ $t('ประเภทการตรวจจับ') }}</label>
          <b-badge
            v-for="type in detectionTypeOptions"
            :key="type.value"
            :variant="filters.detectionType.includes(type.value) ? type.color : 'secondary'"
            class="mr-1 mb-1 cursor-pointer"
            style="font-size: 0.9rem; padding: 0.5rem 0.75rem;"
            @click="toggleDetectionTypeFilter(type.value)"
          >
            <feather-icon
              v-if="filters.detectionType.includes(type.value)"
              icon="CheckIcon"
              size="12"
              class="mr-25"
            />
            {{ $t(type.label) }}
          </b-badge>
        </div>

        <!-- Status Section -->
        <div class="mb-2">
          <label class="d-block mb-1">{{ $t('สถานะ') }}</label>
          <b-badge
            v-for="status in statusOptions"
            :key="status.value"
            :variant="filters.status.includes(status.value) ? status.color : 'secondary'"
            class="mr-1 mb-1 cursor-pointer"
            style="font-size: 0.9rem; padding: 0.5rem 0.75rem;"
            @click="toggleStatusFilter(status.value)"
          >
            <feather-icon
              v-if="filters.status.includes(status.value)"
              icon="CheckIcon"
              size="12"
              class="mr-25"
            />
            {{ $t(status.label) }}
          </b-badge>
        </div>
      </b-form>
    </b-card>

    <!-- Table Card -->
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
        <h4 class="mb-0">
          {{ $t('รายการกิจกรรมน่าสงสัย') }}
        </h4>
        <b-form-input
          v-model="searchText"
          type="search"
          :placeholder="$t('Search')"
          size="sm"
          class="table-search-input"
          debounce="300"
          @update="onSubmit"
        />
      </b-card-header>
      <b-card-body>
        <b-table
          ref="table"
          responsive
          :items="data"
          :fields="$tFields(columns)"
          :busy="loading"
          :sort-by.sync="pagination.sortBy"
          :sort-desc.sync="pagination.descending"
          bordered
          show-empty
          :empty-text="$t('ไม่พบข้อมูล')"
          @sort-changed="onSortChanged"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle mr-1" />
              <strong>{{ $t('กำลังโหลด...') }}</strong>
            </div>
          </template>

          <template #cell(firstDetectedAt)="row">
            {{ formatDateTime(row.item.firstDetectedAt) }}
          </template>

          <template #cell(detectionType)="row">
            <div class="d-flex align-items-center">
              <feather-icon
                :icon="getDetectionIcon(row.item.detectionType)"
                class="mr-50"
              />
              <div>
                <div>{{ getDetectionLabel(row.item.detectionType) }}</div>
                <small
                  v-if="row.item.detectionType === 'IpClustering' && row.item.ipAddress"
                  class="text-secondary cursor-pointer"
                  @click="copyToClipboard(row.item.ipAddress)"
                >
                  {{ truncateIP(row.item.ipAddress) }}
                </small>
              </div>
            </div>
          </template>

          <template #cell(huayRound)="row">
            <div>{{ huayIdToName[row.item.huayId] || $t('หวย #') + row.item.huayId }}</div>
            <small class="text-secondary">{{ $t('รอบ') }} {{ row.item.roundId }}</small>
          </template>

          <template #cell(users)="row">
            <div class="text-center">
              <template v-if="row.item.usernames && row.item.usernames.length <= 3">
                <div
                  v-for="username in row.item.usernames"
                  :key="username"
                >
                  {{ username }}
                </div>
              </template>
              <template v-else-if="row.item.usernames">
                <template v-if="!expandedUserCells.includes(row.item.id)">
                  <div
                    v-for="username in row.item.usernames.slice(0, 2)"
                    :key="username"
                  >
                    {{ username }}
                  </div>
                  <small
                    class="text-primary cursor-pointer"
                    @click="toggleUserCell(row.item.id)"
                  >
                    (+{{ row.item.usernames.length - 2 }} {{ $t('คน') }})
                  </small>
                </template>
                <template v-else>
                  <div
                    v-for="username in row.item.usernames"
                    :key="username"
                  >
                    {{ username }}
                  </div>
                  <small
                    class="text-primary cursor-pointer"
                    @click="toggleUserCell(row.item.id)"
                  >
                    {{ $t('(ย่อ)') }}
                  </small>
                </template>
              </template>
            </div>
          </template>

          <template #cell(totalAmount)="row">
            {{ formatCurrency(row.item.totalAmount || 0) }}
          </template>

          <template #cell(betCount)="row">
            {{ formatNumber(row.item.betCount || 0) }} {{ $t('รายการ') }}
          </template>

          <template #cell(occurrenceCount)="row">
            {{ row.item.occurrenceCount }} {{ $t('ครั้ง') }}
          </template>

          <template #cell(riskScore)="row">
            <b-badge :variant="getRiskColor(row.item.riskScore)">
              {{ row.item.riskScore }}
            </b-badge>
          </template>

          <template #cell(status)="row">
            <b-badge :variant="getStatusColor(row.item.status)">
              {{ getStatusLabel(row.item.status) }}
            </b-badge>
          </template>

          <template #cell(details)="row">
            <b-button
              v-b-tooltip.hover
              variant="flat-primary"
              size="sm"
              :title="isMaster ? $t('จัดการกิจกรรม') : $t('ดูรายละเอียด')"
              @click="openDetailModal(row.item)"
            >
              <feather-icon :icon="isMaster ? 'ExternalLinkIcon' : 'EyeIcon'" />
            </b-button>
          </template>
        </b-table>

        <b-row>
          <b-col cols="2">
            <b-form-group class="text-nowrap mb-0">
              <label class="d-inline-block mr-50 text-sm-right font-small-4">{{ $t('Per page') }}</label>
              <b-form-select
                v-model="pagination.rowsPerPage"
                size="sm"
                :options="$tOptions(rowsOptions)"
                class="w-50"
                style="min-width: 100px"
                @change="onSubmit"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            lg="8"
            md="6"
            sm="12"
          >
            <b-pagination
              v-model="pagination.page"
              :total-rows="pagination.rowsNumber"
              :per-page="pagination.rowsPerPage"
              align="center"
              size="sm"
              class="my-0"
              @change="onPageChange"
            />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Detail Modal -->
    <b-modal
      v-model="showDetailDialog"
      hide-footer
      centered
      :dialog-class="isDetailDialogMaximized ? 'modal-fullscreen' : 'modal-detail-dialog'"
      :content-class="isDetailDialogMaximized ? 'modal-fullscreen-content' : 'modal-detail-content'"
      body-class="p-0"
      header-class="p-0 border-0"
    >
      <template #modal-header="{ close }">
        <div class="modal-header-custom d-flex align-items-center justify-content-between w-100">
          <div class="d-flex align-items-center">
            <feather-icon
              v-if="selectedActivity"
              :icon="getDetectionIcon(selectedActivity.detectionType)"
              size="18"
              class="mr-50"
            />
            <span class="font-weight-bold">{{ selectedActivity ? getDetectionLabel(selectedActivity.detectionType) : $t('รายละเอียด') }}</span>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              variant="flat-light"
              size="sm"
              class="btn-icon mr-25 text-white btn-maximize-toggle"
              @click="isDetailDialogMaximized = !isDetailDialogMaximized"
            >
              <feather-icon
                :icon="isDetailDialogMaximized ? 'MinimizeIcon' : 'MaximizeIcon'"
                size="16"
              />
            </b-button>
            <b-button
              variant="flat-light"
              size="sm"
              class="btn-icon text-white"
              @click="close()"
            >
              <feather-icon
                icon="XIcon"
                size="16"
              />
            </b-button>
          </div>
        </div>
      </template>
      <template v-if="selectedActivity">
        <!-- Scrollable Content -->
        <div class="modal-scrollable-content">
          <!-- Header Info Bar -->
          <div class="d-flex justify-content-between align-items-center px-1 py-50 bg-light">
            <span class="font-weight-medium small">
              {{ huayIdToName[selectedActivity.huayId] || $t('หวย #') + selectedActivity.huayId }} / {{ $t('รอบ') }} {{ selectedActivity.roundId }}
            </span>
            <div class="d-flex align-items-center flex-wrap">
              <b-badge
                :variant="getRiskColor(selectedActivity.riskScore)"
                pill
                class="mr-50"
              >
                Risk: {{ selectedActivity.riskScore }}
              </b-badge>
              <b-badge
                :variant="getStatusColor(selectedActivity.status)"
                pill
              >
                {{ getStatusLabel(selectedActivity.status) }}
              </b-badge>
              <small class="ml-75 text-secondary">{{ formatDateTime(selectedActivity.firstDetectedAt) }}</small>
            </div>
          </div>

          <div class="p-1">
            <template v-if="selectedActivity.details">
              <!-- IP Address & Agent -->
              <b-row class="mb-75">
                <b-col
                  cols="6"
                  sm="6"
                >
                  <feather-icon
                    icon="GlobeIcon"
                    size="14"
                    class="mr-50 text-primary"
                  />
                  <small class="text-secondary">IP Address</small>
                  <div class="font-weight-bold">
                    {{ selectedActivity.ipAddress }}
                  </div>
                </b-col>
                <b-col
                  cols="6"
                  sm="6"
                  class="text-right"
                >
                  <small class="text-secondary">{{ $t('ตัวแทน') }}</small>
                  <div class="font-weight-bold">
                    {{ selectedActivity.agent || '-' }}
                  </div>
                </b-col>
              </b-row>

              <!-- Summary Cards - Compact -->
              <b-row class="mb-75 mx-n25">
                <b-col
                  cols="4"
                  class="px-25"
                >
                  <div class="text-center p-50 border rounded">
                    <feather-icon
                      icon="UsersIcon"
                      size="14"
                      class="text-primary"
                    />
                    <div
                      class="text-secondary small"
                      style="font-size: 10px;"
                    >
                      {{ $t('ผู้ใช้') }}
                    </div>
                    <div class="font-weight-bold">
                      {{ selectedActivity.details.summary.uniqueUsers }}
                    </div>
                  </div>
                </b-col>
                <b-col
                  cols="4"
                  class="px-25"
                >
                  <div class="text-center p-50 border rounded">
                    <feather-icon
                      icon="ListIcon"
                      size="14"
                      class="text-primary"
                    />
                    <div
                      class="text-secondary small"
                      style="font-size: 10px;"
                    >
                      {{ $t('รายการ') }}
                    </div>
                    <div class="font-weight-bold">
                      {{ selectedActivity.details.summary.totalNumbers }}
                    </div>
                  </div>
                </b-col>
                <b-col
                  cols="4"
                  class="px-25"
                >
                  <div class="text-center p-50 border rounded">
                    <feather-icon
                      icon="DollarSignIcon"
                      size="14"
                      class="text-primary"
                    />
                    <div
                      class="text-secondary small"
                      style="font-size: 10px;"
                    >
                      {{ $t('บาท') }}
                    </div>
                    <div class="font-weight-bold">
                      {{ formatNumber(selectedActivity.details.summary.totalAmount) }}
                    </div>
                  </div>
                </b-col>
              </b-row>

              <!-- Bet Type Summary - Compact -->
              <div
                v-if="overallBetTypeSummary.length > 0"
                class="mb-75"
              >
                <small class="d-block mb-50 font-weight-bold">{{ $t('สรุปการแทงตามประเภท') }} ({{ overallBetTypeSummary.length }} {{ $t('ประเภท') }})</small>
                <div
                  class="d-flex flex-wrap"
                  style="gap: 6px;"
                >
                  <div
                    v-for="typeSum in overallBetTypeSummary"
                    :key="typeSum.typeName"
                    class="d-flex align-items-center px-50 py-25 bg-light rounded"
                    style="gap: 4px;"
                  >
                    <b-badge
                      :variant="getBetTypeColor(typeSum.typeName)"
                      class="small"
                    >
                      {{ typeSum.typeName }}
                    </b-badge>
                    <small class="font-weight-bold">{{ typeSum.count }}</small>
                    <small class="text-secondary">{{ $t('เลข') }}</small>
                    <small class="text-secondary">|</small>
                    <small class="font-weight-bold">{{ formatNumber(typeSum.totalAmount) }}฿</small>
                  </div>
                </div>
              </div>

              <!-- Transaction Timeline - Compact -->
              <div>
                <small class="d-block mb-50 font-weight-bold">{{ $t('รายละเอียดผู้ใช้') }} ({{ selectedActivity.details.transactions.length }} {{ $t('การแทง') }})</small>

                <div
                  v-for="(transaction, index) in selectedActivity.details.transactions"
                  :key="transaction.betId || index"
                  class="border rounded mb-50 p-50"
                  :style="{ borderLeftWidth: '3px !important', borderLeftColor: getTransactionBorderColor(transaction) + ' !important' }"
                >
                  <!-- Transaction Header -->
                  <div class="d-flex justify-content-between align-items-center mb-25">
                    <div class="d-flex align-items-center">
                      <feather-icon
                        icon="UserIcon"
                        size="12"
                        class="mr-25"
                      />
                      <span class="font-weight-bold small">{{ transaction.username }}</span>
                    </div>
                    <small
                      class="text-secondary"
                      style="font-size: 10px;"
                    >
                      {{ formatDateTime(transaction.betTime) }}
                    </small>
                  </div>

                  <!-- Bet Summary -->
                  <div class="mb-25 d-flex justify-content-between align-items-center">
                    <div>
                      <small class="text-secondary">{{ $t('ยอดแทง') }} </small>
                      <small class="font-weight-bold">{{ formatNumber(transaction.totalAmount || 0) }}฿</small>
                      <small class="text-secondary"> | </small>
                      <small class="font-weight-bold">{{ transaction.numberCount || 0 }}</small>
                      <small class="text-secondary"> {{ $t('เลข') }}</small>
                    </div>
                    <div
                      v-if="transaction.device"
                      class="d-flex align-items-center"
                    >
                      <feather-icon
                        :icon="getDeviceIcon(transaction.device.type)"
                        size="12"
                        class="mr-25 text-secondary"
                      />
                      <small
                        class="text-secondary"
                        style="font-size: 10px;"
                      >{{ transaction.device.info }}</small>
                    </div>
                  </div>

                  <!-- Numbers by Bet Type -->
                  <div
                    v-if="transaction.numbers && transaction.numbers.length > 0"
                    class="d-flex flex-wrap"
                    style="gap: 6px;"
                  >
                    <div
                      v-for="group in groupNumbersByType(transaction.numbers)"
                      :key="group.typeName"
                      style="flex: 0 1 auto; min-width: 0; width: fit-content;"
                    >
                      <!-- Bet Type Header -->
                      <div
                        class="d-flex align-items-center justify-content-between cursor-pointer px-50 py-25 bg-light rounded"
                        @click="toggleBetType(index, group.typeName)"
                      >
                        <div
                          class="d-flex align-items-center"
                          style="gap: 6px;"
                        >
                          <b-badge
                            :variant="getBetTypeColor(group.typeName)"
                            class="small"
                          >
                            {{ group.typeName }}
                          </b-badge>
                          <small class="text-secondary">
                            {{ group.numbers.length }} {{ $t('เลข') }} | {{ formatNumber(group.totalAmount) }}฿
                          </small>
                        </div>
                        <feather-icon
                          :icon="expandedBetTypes[`${index}-${group.typeName}`] ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                          size="12"
                          class="ml-50"
                        />
                      </div>

                      <!-- Numbers Grid (Expandable) -->
                      <div
                        :style="!expandedBetTypes[`${index}-${group.typeName}`]
                          ? 'height: 0; margin: 0; overflow: hidden; visibility: hidden;'
                          : 'margin-top: 6px;'"
                      >
                        <div
                          class="d-flex flex-wrap"
                          style="gap: 6px;"
                        >
                          <div
                            v-for="(num, idx) in group.numbers"
                            :key="idx"
                            class="text-center px-50 py-25 bg-light rounded"
                            style="min-width: 60px; font-family: monospace; font-size: 12px;"
                          >
                            <div>{{ num.number }}</div>
                            <small
                              v-if="num.amount"
                              class="text-secondary"
                              style="font-size: 9px;"
                            >{{ num.amount }}฿</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <template v-else-if="loadingDetail">
              <div class="text-center py-3">
                <b-spinner
                  small
                  class="mb-50"
                />
                <div class="text-secondary small">
                  {{ $t('กำลังโหลดข้อมูล...') }}
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Fixed Footer -->
        <div class="modal-fixed-footer">
          <!-- Activity Timeline Footer -->
          <div
            v-if="selectedActivity.markedBy"
            class="px-1 py-50 bg-light"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="ClockIcon"
                size="14"
                class="mr-50 text-secondary"
              />
              <div>
                <small class="font-weight-bold">{{ selectedActivity.markedBy }}</small>
                <small class="text-secondary">
                  {{ $t('อัปเดตสถานะเป็น') }} "{{ getStatusLabel(selectedActivity.status) }}"
                  {{ $t('เมื่อ') }} {{ formatDateTime(selectedActivity.markedAt) }}
                </small>
                <div
                  v-if="selectedActivity.notes"
                  class="font-italic text-secondary small"
                >
                  "{{ selectedActivity.notes }}"
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="px-1 py-50 bg-light"
          >
            <small class="text-secondary">
              <feather-icon
                icon="InfoIcon"
                size="12"
                class="mr-25"
              />
              {{ $t('ยังไม่มีการตรวจสอบ') }}
            </small>
          </div>

          <!-- Action Buttons -->
          <div class="modal-action-footer d-flex justify-content-end p-1 border-top bg-white">
            <b-button
              variant="outline-secondary"
              size="sm"
              class="mr-50"
              @click="showDetailDialog = false"
            >
              {{ $t('ปิด') }}
            </b-button>
            <b-button
              v-if="isMaster"
              variant="primary"
              size="sm"
              @click="openStatusModal"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-25"
              />
              {{ $t('อัปเดตสถานะ') }}
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- Status Update Modal - Master Only -->
    <b-modal
      v-if="isMaster"
      v-model="showStatusDialog"
      :title="$t('อัปเดตสถานะ')"
      centered
      hide-footer
    >
      <template v-if="selectedActivity">
        <div class="mb-3">
          <div class="text-secondary small">
            {{ $t('กิจกรรม') }}
          </div>
          <strong>{{ getDetectionLabel(selectedActivity.detectionType) }}</strong>
          <div class="text-secondary small">
            {{ huayIdToName[selectedActivity.huayId] || $t('หวย #') + selectedActivity.huayId }} / {{ $t('รอบ') }} {{ selectedActivity.roundId }}
          </div>
        </div>

        <hr>

        <b-form-group :label="$t('สถานะใหม่')">
          <v-select
            v-model="markForm.status"
            :options="$tOptions(statusOptions)"
            label="label"
            :reduce="opt => opt.value"
            :placeholder="$t('เลือกสถานะ')"
          />
        </b-form-group>

        <b-form-group :label="$t('หมายเหตุ')">
          <b-form-textarea
            v-model="markForm.notes"
            rows="3"
            :placeholder="$t('กรอกเหตุผลหรือรายละเอียดการตัดสินใจ')"
          />
        </b-form-group>

        <div class="text-right mt-3">
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="showStatusDialog = false"
          >
            {{ $t('ยกเลิก') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="!markForm.status || markLoading"
            @click="markActivity"
          >
            <b-spinner
              v-if="markLoading"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              class="mr-50"
            />
            {{ $t('บันทึก') }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BButton,
  BTable,
  BPagination,
  BSpinner,
  BBadge,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import lottoService from '@/service/lottoService'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BButton,
    BTable,
    BPagination,
    BSpinner,
    BBadge,
    BModal,
    flatPickr,
    vSelect,
    AgentSelector,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      data: [],
      loading: false,
      selectedAgent: null,
      loadingDetail: false,
      markLoading: false,
      searchText: '',
      pagination: {
        page: 1,
        rowsPerPage: 50,
        rowsNumber: 0,
        sortBy: 'firstDetectedAt',
        descending: true,
      },
      filters: {
        status: ['New', 'Reviewing'],
        detectionType: [],
        huayName: null,
        roundId: null,
        startDate: null,
        endDate: null,
      },
      dateConfig: {
        disableMobile: true,
        enableTime: false,
        dateFormat: 'Y-m-d',
      },
      gameTypes: ['ทั้งหมด'],
      gameTypesMapper: {},
      huayIdToName: {},
      selectedActivity: null,
      showDetailDialog: false,
      showStatusDialog: false,
      isDetailDialogMaximized: false,
      expandedUserCells: [],
      expandedBetTypes: {},
      markForm: {
        status: null,
        notes: '',
      },
      rowsOptions: [10, 25, 50, 100, 250],
      statusOptions: [
        { label: 'ใหม่', value: 'New', color: 'status-primary' },
        { label: 'กำลังตรวจสอบ', value: 'Reviewing', color: 'status-warning' },
        { label: 'ยืนยันการปั้ม', value: 'Confirmed', color: 'status-danger' },
        { label: 'ไม่ใช่การปั้ม', value: 'FalsePositive', color: 'status-success' },
        { label: 'บล็อกแล้ว', value: 'Blocked', color: 'status-dark' },
      ],
      detectionTypeOptions: [
        { label: 'IP Clustering', value: 'IpClustering', color: 'detection-purple' },
        { label: 'Sequential Numbers', value: 'SequentialNumbers', color: 'detection-indigo' },
        { label: 'High Volume', value: 'HighVolume', color: 'detection-deeporange' },
        { label: 'Coverage', value: 'Coverage', color: 'detection-teal' },
        { label: 'Repeat Betting', value: 'RepeatBetting', color: 'detection-pink' },
      ],
    }
  },
  computed: {
    isMaster() {
      return this.$store.state.app.isMaster(this.$store)
    },
    columns() {
      return [
        {
          key: 'firstDetectedAt',
          label: 'ตรวจพบครั้งแรก',
          sortable: true,
          thStyle: { minWidth: '150px' },
        },
        {
          key: 'detectionType',
          label: 'ประเภท',
          sortable: true,
          thClass: 'text-center',
          thStyle: { minWidth: '200px' },
        },
        {
          key: 'huayRound',
          label: 'หวย/รอบ',
          sortable: false,
          thClass: 'text-center',
          thStyle: { minWidth: '175px' },
        },
        {
          key: 'agent',
          label: 'ตัวแทน',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { minWidth: '150px' },
        },
        {
          key: 'users',
          label: 'ผู้ใช้',
          sortable: false,
          thClass: 'text-center',
          thStyle: { minWidth: '140px' },
        },
        {
          key: 'totalAmount',
          label: 'ยอดเงินรวม',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { minWidth: '140px' },
        },
        {
          key: 'betCount',
          label: 'รายการแทง',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { minWidth: '140px' },
        },
        {
          key: 'occurrenceCount',
          label: 'จำนวนครั้ง',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { minWidth: '140px' },
        },
        {
          key: 'riskScore',
          label: 'Risk',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { minWidth: '100px' },
        },
        {
          key: 'status',
          label: 'สถานะ',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { minWidth: '100px' },
        },
        {
          key: 'details',
          label: this.isMaster ? 'จัดการ' : 'ดู',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { minWidth: '80px' },
        },
      ]
    },
    overallBetTypeSummary() {
      if (!this.selectedActivity?.details?.transactions) {
        return []
      }

      const summary = {}
      const { transactions } = this.selectedActivity.details

      transactions.forEach(transaction => {
        if (transaction.numbers) {
          transaction.numbers.forEach(numObj => {
            const typeName = numObj.typeName || 'ไม่ระบุประเภท'

            if (!summary[typeName]) {
              summary[typeName] = {
                typeName,
                count: 0,
                totalAmount: 0,
              }
            }

            summary[typeName].count += 1
            summary[typeName].totalAmount += numObj.amount || 0
          })
        }
      })

      return Object.values(summary).sort((a, b) => b.totalAmount - a.totalAmount)
    },
  },
  async mounted() {
    this.loadFiltersFromStorage()
    await this.loadHuayTypes()
    this.onSubmit()
  },
  methods: {
    async loadHuayTypes() {
      try {
        const [res, res2] = await Promise.all([
          lottoService.getAuthen('huay/dict'),
          lottoService.getAuthen('yeekee/dict'),
        ])

        if (res.status === 200) {
          res.data.forEach(v => {
            this.gameTypes.push(v.name)
            this.gameTypesMapper[v.name] = v.id
            this.huayIdToName[v.id] = v.name
          })
        }
        if (res2.status === 200) {
          res2.data.forEach(v => {
            this.gameTypes.push(v.name)
            this.gameTypesMapper[v.name] = v.id
            this.huayIdToName[v.id] = v.name
          })
        }
      } catch (error) {
        // Error loading huay types
      }
    },

    loadFiltersFromStorage() {
      try {
        const savedFilters = localStorage.getItem('fraudDetectionFilters')
        if (savedFilters) {
          const parsed = JSON.parse(savedFilters)
          if (parsed.status) {
            this.filters.status = parsed.status
          }
          if (parsed.detectionType) {
            this.filters.detectionType = parsed.detectionType
          }
        }
      } catch (error) {
        // Error loading filters
      }
    },

    saveFiltersToStorage() {
      try {
        const filtersToSave = {
          status: this.filters.status,
          detectionType: this.filters.detectionType,
        }
        localStorage.setItem('fraudDetectionFilters', JSON.stringify(filtersToSave))
      } catch (error) {
        // Error saving filters
      }
    },

    toggleStatusFilter(status) {
      const index = this.filters.status.indexOf(status)
      if (index > -1) {
        this.filters.status.splice(index, 1)
      } else {
        this.filters.status.push(status)
      }
      this.saveFiltersToStorage()
      this.onSubmit()
    },

    toggleDetectionTypeFilter(type) {
      const index = this.filters.detectionType.indexOf(type)
      if (index > -1) {
        this.filters.detectionType.splice(index, 1)
      } else {
        this.filters.detectionType.push(type)
      }
      this.saveFiltersToStorage()
      this.onSubmit()
    },

    async onSubmit() {
      this.pagination.page = 1
      await this.loadData()
    },

    async onPageChange(page) {
      this.pagination.page = page
      await this.loadData()
    },

    async onSortChanged(ctx) {
      this.pagination.sortBy = ctx.sortBy
      this.pagination.descending = ctx.sortDesc
      await this.loadData()
    },

    async loadData() {
      this.loading = true
      try {
        const params = new URLSearchParams()

        // Add filters
        if (this.filters.status.length > 0) {
          this.filters.status.forEach(status => params.append('status', status))
        }
        if (this.filters.detectionType.length > 0) {
          this.filters.detectionType.forEach(type => params.append('detectionType', type))
        }
        if (this.filters.huayName && this.filters.huayName !== 'ทั้งหมด') {
          const huayId = this.gameTypesMapper[this.filters.huayName]
          if (huayId) {
            params.append('huayId', huayId)
          }
        }
        if (this.filters.roundId) {
          params.append('roundId', this.filters.roundId)
        }
        if (this.filters.startDate) {
          params.append('startDate', this.filters.startDate)
        }
        if (this.filters.endDate) {
          params.append('endDate', this.filters.endDate)
        }
        if (this.searchText) {
          params.append('search', this.searchText)
        }
        if (this.selectedAgent) {
          params.append('agent', this.selectedAgent.code)
        }

        // Add pagination
        const startRow = (this.pagination.page - 1) * this.pagination.rowsPerPage
        params.append('skip', startRow)
        params.append('limit', this.pagination.rowsPerPage)

        // Add sorting
        if (this.pagination.sortBy) {
          params.append('sortBy', this.pagination.sortBy)
          params.append('sortOrder', this.pagination.descending ? 'desc' : 'asc')
        }

        const res = await lottoService.getAuthen(`fraud-detection/suspicious-activities?${params.toString()}`)

        if (res.status === 200 && res.data) {
          this.data = res.data.data || []
          this.pagination.rowsNumber = res.data.pagination?.totalItems || 0
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('ไม่สามารถโหลดข้อมูลได้'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },

    async openDetailModal(activity) {
      this.selectedActivity = activity
      this.isDetailDialogMaximized = false
      this.showDetailDialog = true
      this.expandedBetTypes = {}

      this.loadingDetail = true
      try {
        const res = await lottoService.getAuthen(
          `fraud-detection/activity-detail/${activity.id}`,
        )

        if (res.status === 200 && res.data) {
          this.selectedActivity = res.data

          // Default expand all bet types
          this.$nextTick(() => {
            const expandState = {}
            const transactions = this.selectedActivity?.details?.transactions || []
            transactions.forEach((transaction, transactionIndex) => {
              const groups = this.groupNumbersByType(transaction.numbers || [])
              groups.forEach(group => {
                const key = `${transactionIndex}-${group.typeName}`
                expandState[key] = true
              })
            })
            this.expandedBetTypes = expandState
          })
        }
      } catch (error) {
        // Error loading detail
      } finally {
        this.loadingDetail = false
      }
    },

    openStatusModal() {
      if (!this.selectedActivity) return

      this.markForm.status = this.selectedActivity.status
      this.markForm.notes = this.selectedActivity.notes || ''
      this.showStatusDialog = true
    },

    async markActivity() {
      if (!this.selectedActivity || !this.markForm.status) return

      this.$bvModal.msgBoxConfirm(this.$t('คุณต้องการอัปเดตสถานะเป็น "{status}" ใช่หรือไม่?', {
        status: this.getStatusLabel(this.markForm.status),
      }), {
        title: this.$t('ยืนยันการอัปเดต'),
        okVariant: 'primary',
        okTitle: this.$t('ยืนยัน'),
        cancelTitle: this.$t('ยกเลิก'),
        cancelVariant: 'outline-secondary',
        centered: true,
      }).then(async value => {
        if (value) {
          this.markLoading = true
          try {
            const res = await lottoService.postAuthen(
              'fraud-detection/mark',
              {
                activityId: this.selectedActivity.id,
                status: this.markForm.status,
                notes: this.markForm.notes,
              },
            )

            if (res.status === 200) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t('อัปเดตสถานะสำเร็จ'),
                  icon: 'CheckCircleIcon',
                  variant: 'success',
                },
              })
              this.showStatusDialog = false
              this.showDetailDialog = false
              this.onSubmit()
            } else {
              throw new Error('Failed to update status')
            }
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: this.$t('ไม่สามารถอัปเดตสถานะได้'),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          } finally {
            this.markLoading = false
          }
        }
      })
    },

    toggleUserCell(rowId) {
      const index = this.expandedUserCells.indexOf(rowId)
      if (index > -1) {
        this.expandedUserCells.splice(index, 1)
      } else {
        this.expandedUserCells.push(rowId)
      }
    },

    toggleBetType(transactionIndex, typeName) {
      const key = `${transactionIndex}-${typeName}`
      this.$set(this.expandedBetTypes, key, !this.expandedBetTypes[key])
    },

    groupNumbersByType(numbers) {
      if (!numbers || numbers.length === 0) return []

      const grouped = {}
      numbers.forEach(numObj => {
        const typeName = numObj.typeName || 'ไม่ระบุประเภท'
        if (!grouped[typeName]) {
          grouped[typeName] = {
            typeName,
            numbers: [],
            totalAmount: 0,
          }
        }
        grouped[typeName].numbers.push(numObj)
        grouped[typeName].totalAmount += numObj.amount || 0
      })

      return Object.values(grouped).sort((a, b) => b.totalAmount - a.totalAmount)
    },

    truncateIP(ip) {
      if (!ip) return ''
      if (ip.length > 20) {
        const parts = ip.split(':')
        if (parts.length >= 3) {
          return `${parts[0]}:${parts[1]}...${parts[parts.length - 1]}`
        }
      }
      return ip
    },

    copyToClipboard(text) {
      if (!text) return

      navigator.clipboard.writeText(text).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('คัดลอก IP Address แล้ว'),
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
      }).catch(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('ไม่สามารถคัดลอกได้'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
    },

    getDetectionIcon(type) {
      const icons = {
        IpClustering: 'GlobeIcon',
        SequentialNumbers: 'ListIcon',
        HighVolume: 'TrendingUpIcon',
        Coverage: 'GridIcon',
        RepeatBetting: 'RepeatIcon',
      }
      return icons[type] || 'HelpCircleIcon'
    },

    getDetectionLabel(type) {
      const labels = {
        IpClustering: 'IP Clustering',
        SequentialNumbers: 'Sequential Numbers',
        HighVolume: 'High Volume',
        Coverage: 'Coverage',
        RepeatBetting: 'Repeat Betting',
      }
      return labels[type] || type
    },

    getRiskColor(score) {
      if (score >= 61) return 'risk-danger'
      if (score >= 31) return 'risk-warning'
      return 'risk-success'
    },

    getStatusColor(status) {
      const statusOption = this.statusOptions.find(opt => opt.value === status)
      return statusOption ? statusOption.color : 'secondary'
    },

    getStatusLabel(status) {
      const statusOption = this.statusOptions.find(opt => opt.value === status)
      return statusOption ? statusOption.label : status
    },

    getBetTypeColor(typeName) {
      const colorMap = {
        สามตัวบน: 'bet-indigo',
        สามตัวโต้ด: 'bet-purple',
        สองตัวบน: 'bet-teal',
        สองตัวล่าง: 'bet-cyan',
        วิ่งบน: 'bet-bluegrey',
        วิ่งล่าง: 'bet-brown',
      }
      return colorMap[typeName] || 'secondary'
    },

    getDeviceIcon(deviceType) {
      if (!deviceType) return 'MonitorIcon'
      const type = deviceType.toLowerCase()
      if (type === 'smartphone') return 'SmartphoneIcon'
      if (type === 'tablet') return 'TabletIcon'
      if (type === 'desktop') return 'MonitorIcon'
      return 'MonitorIcon'
    },

    getTransactionBorderColor(transaction) {
      // Simple color based on amount
      if (transaction.totalAmount >= 10000) return '#ea5455'
      if (transaction.totalAmount >= 5000) return '#ff9f43'
      return '#28c76f'
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'

      if (typeof dateTime === 'string' && dateTime.match(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/)) {
        return dateTime
      }

      const date = new Date(dateTime)
      if (Number.isNaN(date.getTime())) return '-'

      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const seconds = date.getSeconds().toString().padStart(2, '0')
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
    },

    formatNumber(num) {
      if (!num) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    formatCurrency(amount) {
      if (!amount) return '0 ฿'
      return `${this.formatNumber(amount)} ฿`
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.cursor-pointer {
  cursor: pointer;
}

// Table text-secondary styling
.table {
  .text-secondary,
  small.text-secondary {
    color: #5d555a !important;
  }
}

.dark-layout {
  .table {
    .text-secondary,
    small.text-secondary {
      color: #9ca0a8 !important;
    }
  }
}

// Table header search responsive
.table-search-input {
  width: 200px;
  flex: 0 0 auto;
}

@media (max-width: 767.98px) {
  .table-search-input {
    width: 100% !important;
    flex: 1 1 100% !important;
    margin-top: 0.5rem;
  }
}

// Slightly darker bg-light for modal (5% darker)
.modal-detail-dialog .bg-light,
.modal-fullscreen .bg-light {
  background-color: #eef0f2 !important;
}

// Dark mode support
.dark-layout {
  .modal-detail-dialog,
  .modal-fullscreen {
    .bg-light {
      background-color: rgba(255, 255, 255, 0.08) !important;
    }

    .border {
      border-color: rgba(255, 255, 255, 0.15) !important;
    }

    .modal-content {
      background-color: #283046 !important;
      color: #d0d2d6 !important;
    }

    .text-secondary {
      color: #a6aab4 !important;
    }

    // Main text color
    .font-weight-bold,
    .font-weight-medium,
    strong,
    span,
    div {
      color: #d0d2d6;
    }

    // Summary cards text
    .text-center .font-weight-bold {
      color: #fff !important;
    }

    // Small text
    small {
      color: #a6aab4 !important;
    }

    small.text-secondary {
      color: #8a8e99 !important;
    }

    // Labels
    label {
      color: #d0d2d6 !important;
    }
  }

  .modal-header-custom {
    background-color: $primary;
    color: #fff !important;
  }

  .modal-action-footer {
    background-color: #283046 !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
  }

}

// Custom Bet Type Colors (matching Quasar/lt432)
.badge-bet-indigo {
  background-color: #3f51b5 !important;
  color: white !important;
}

.badge-bet-purple {
  background-color: #9c27b0 !important;
  color: white !important;
}

.badge-bet-teal {
  background-color: #009688 !important;
  color: white !important;
}

.badge-bet-cyan {
  background-color: #00bcd4 !important;
  color: white !important;
}

.badge-bet-bluegrey {
  background-color: #607d8b !important;
  color: white !important;
}

.badge-bet-brown {
  background-color: #795548 !important;
  color: white !important;
}

// Custom Detection Type Colors (matching Quasar/lt432)
.badge-detection-purple {
  background-color: #9c27b0 !important;
  color: white !important;
}

.badge-detection-indigo {
  background-color: #3f51b5 !important;
  color: white !important;
}

.badge-detection-deeporange {
  background-color: #ff5722 !important;
  color: white !important;
}

.badge-detection-teal {
  background-color: #009688 !important;
  color: white !important;
}

.badge-detection-pink {
  background-color: #e91e63 !important;
  color: white !important;
}

// Custom Status Colors
.badge-status-primary {
  background-color: rgba(var(--hdg-brand-primary-rgb), 0.11) !important;
  border: 1px solid rgba(var(--hdg-brand-primary-rgb), 0.16);
  color: var(--hdg-brand-primary) !important;
}

.badge-status-warning {
  background-color: rgba(255, 159, 67, 0.13) !important;
  border: 1px solid rgba(255, 159, 67, 0.18);
  color: #d78116 !important;
}

.badge-status-danger {
  background-color: rgba(234, 84, 85, 0.12) !important;
  border: 1px solid rgba(234, 84, 85, 0.18);
  color: #d83f40 !important;
}

.badge-status-success {
  background-color: rgba(40, 199, 111, 0.12) !important;
  border: 1px solid rgba(40, 199, 111, 0.18);
  color: #1f9d5a !important;
}

.badge-status-dark {
  background-color: rgba(75, 75, 75, 0.1) !important;
  border: 1px solid rgba(75, 75, 75, 0.16);
  color: #5d555a !important;
}

.dark-layout {
  .badge-status-primary {
    background-color: rgba(var(--hdg-brand-primary-emphasis-rgb), 0.16) !important;
    border-color: rgba(var(--hdg-brand-primary-emphasis-rgb), 0.24);
    color: rgba(var(--hdg-brand-primary-emphasis-rgb), 0.94) !important;
  }

  .badge-status-warning {
    background-color: rgba(255, 159, 67, 0.16) !important;
    border-color: rgba(255, 159, 67, 0.24);
    color: #ffb766 !important;
  }

  .badge-status-danger {
    background-color: rgba(234, 84, 85, 0.16) !important;
    border-color: rgba(234, 84, 85, 0.24);
    color: #ff7b7c !important;
  }

  .badge-status-success {
    background-color: rgba(40, 199, 111, 0.15) !important;
    border-color: rgba(40, 199, 111, 0.22);
    color: #4fe093 !important;
  }

  .badge-status-dark {
    background-color: rgba(180, 183, 189, 0.11) !important;
    border-color: rgba(180, 183, 189, 0.18);
    color: #c7cad1 !important;
  }
}

// Custom Risk Colors
.badge-risk-success {
  background-color: #28c76f !important;
  color: white !important;
}

.badge-risk-warning {
  background-color: #f39c12 !important;
  color: white !important;
}

.badge-risk-danger {
  background-color: #ea5455 !important;
  color: white !important;
}

// Modal Header Custom
.modal-header-custom {
  background-color: $primary;
  color: white;
  padding: 0.75rem 1rem;
  border-top-left-radius: 0.358rem;
  border-top-right-radius: 0.358rem;

  // Header buttons hover effect
  .btn-icon {
    opacity: 0.8;
    transition: opacity 0.15s ease, background-color 0.15s ease;

    &:hover {
      opacity: 1;
      background-color: rgba(255, 255, 255, 0.2) !important;
    }
  }
}

// Detail Modal - Same size as lt432 (836px)
.modal-detail-dialog {
  max-width: 836px !important;
  width: 100% !important;
  margin: 1.75rem auto !important;
}

.modal-detail-content {
  min-height: 500px;
  max-height: calc(100vh - 3.5rem);
  display: flex !important;
  flex-direction: column !important;
}

.modal-detail-content .modal-body {
  flex: 1 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  padding: 0 !important;
}

.modal-detail-content .modal-scrollable-content {
  flex: 1 !important;
  overflow-y: auto !important;
}

.modal-detail-content .modal-fixed-footer {
  flex-shrink: 0 !important;
}

// Fullscreen Modal Styles
.modal-fullscreen {
  max-width: 100% !important;
  width: 100% !important;
  height: 100% !important;
  margin: 0 !important;
}

.modal-fullscreen-content {
  height: 100% !important;
  max-height: 100% !important;
  border-radius: 0 !important;
  border: none !important;
  display: flex !important;
  flex-direction: column !important;
}

.modal-fullscreen .modal-header {
  flex-shrink: 0 !important;
}

.modal-fullscreen .modal-body {
  flex: 1 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  padding: 0 !important;
}

.modal-fullscreen .modal-header-custom {
  border-radius: 0 !important;
}

.modal-fullscreen .modal-scrollable-content {
  flex: 1 !important;
  overflow-y: auto !important;
}

.modal-fullscreen .modal-fixed-footer {
  flex-shrink: 0 !important;
}

.modal-fullscreen .modal-action-footer {
  margin-top: 0 !important;
  flex-shrink: 0 !important;
}

// Override modal dialog for fullscreen
.modal.show .modal-dialog.modal-fullscreen {
  max-width: 100vw !important;
  width: 100vw !important;
  margin: 0 !important;
  padding: 0 !important;
  height: 100vh !important;
  min-height: 100vh !important;
  transform: none !important;
  display: flex !important;
  align-items: stretch !important;
}

.modal.show .modal-dialog.modal-fullscreen.modal-dialog-centered {
  min-height: 100vh !important;
  align-items: stretch !important;
}

.modal.show .modal-dialog.modal-fullscreen .modal-content {
  height: 100vh !important;
  min-height: 100vh !important;
  flex: 1 !important;
}

// Responsive - Force fullscreen when screen < modal width (836px)
@media (max-width: 836px) {
  // Hide maximize button - already fullscreen
  .btn-maximize-toggle {
    display: none !important;
  }

  // Remove modal padding
  .modal {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .modal-detail-dialog {
    max-width: 100vw !important;
    width: 100vw !important;
    height: 100vh !important;
    margin: 0 !important;
    padding: 0 !important;
    left: 0 !important;
    right: 0 !important;
  }

  .modal-detail-content {
    width: 100vw !important;
    height: 100vh !important;
    max-height: 100vh !important;
    min-height: 100vh !important;
    border-radius: 0 !important;
    border: none !important;
  }

  .modal-detail-content .modal-header-custom {
    border-radius: 0 !important;
  }

  .modal.show .modal-dialog.modal-detail-dialog {
    max-width: 100vw !important;
    width: 100vw !important;
    margin: 0 !important;
    padding: 0 !important;
    height: 100vh !important;
    min-height: 100vh !important;
    transform: none !important;
    display: flex !important;
    align-items: stretch !important;
    left: 0 !important;
  }

  .modal.show .modal-dialog.modal-detail-dialog.modal-dialog-centered {
    min-height: 100vh !important;
    align-items: stretch !important;
  }

  .modal.show .modal-dialog.modal-detail-dialog .modal-content,
  .modal.show .modal-dialog.modal-detail-dialog .modal-detail-content {
    width: 100vw !important;
    height: 100vh !important;
    min-height: 100vh !important;
    border-radius: 0 !important;
    flex: 1 !important;
  }
}
</style>
